import { phoneNumbersForTenant } from '@api/queries/phoneNumbersForTenant';
import { useQuery } from '@apollo/client';
import useTenantName from '@lib/hooks/useTenantName';
import React, { useMemo } from 'react';
import { useOutboundCallerIdTable } from '../OutboundCallerIdTable/hooks/useOutboundCallerIdTable';
import { getAreaCode, getPhomeNumberItems } from '../phone-umbers-utils';
import { OutboundCallerIdTableContext } from './OutboundCallerIdTableContext';

export const OutboundCallerIdTableProvider: React.FC<{
  children: React.ReactNode;
  tenantId?: string;
  tenantName?: string;
  selectedLine: string;
  directLine?: string;
  isDynamic?: boolean;
  enableCustomOutboundCallerId?: boolean;
  outgoingCallerId?: string;
}> = ({
  children,
  tenantId,
  tenantName,
  selectedLine,
  directLine,
  isDynamic,
  enableCustomOutboundCallerId,
  outgoingCallerId,
}) => {
  const accountName = useTenantName(tenantId ?? '', tenantName ?? '');
  const isLoadPhoneNumbers = enableCustomOutboundCallerId && !!tenantId;
  const { data, loading, error, refetch } = useQuery(phoneNumbersForTenant, {
    variables: {
      tenantId: tenantId ?? '',
      filter: {
        excludeDirectDials: true,
      },
    },
    skip: !isLoadPhoneNumbers,
    fetchPolicy: 'cache-first',
  });

  const loadingTable = useMemo(() => {
    return loading || !selectedLine;
  }, [loading, selectedLine]);

  const { items, selectedItem } = useMemo(() => {
    if (isLoadPhoneNumbers) {
      if (loadingTable || !data?.phoneNumbersForTenant) {
        return {
          items: [],
          selectedItem: null,
        };
      }
      return getPhomeNumberItems({
        data: data?.phoneNumbersForTenant ?? [],
        selectedLine,
        isDynamic,
        directLine,
        outgoingCallerId,
      });
    }

    return getPhomeNumberItems({
      data: [],
      selectedLine,
      isDynamic,
      directLine,
      outgoingCallerId,
    });
  }, [
    data,
    loadingTable,
    selectedLine,
    isDynamic,
    directLine,
    isLoadPhoneNumbers,
    outgoingCallerId,
  ]);

  const state = useOutboundCallerIdTable({
    data: items,
    initSelectedItem: selectedItem,
    loading: loadingTable,
  });

  const areaCodesOptions = useMemo(() => {
    const areaCodes = new Set<string>();
    items?.forEach((item) => {
      if (item.number) {
        const areaCode = getAreaCode(item.number);
        if (areaCode) {
          areaCodes.add(areaCode);
        }
      }
    });
    return Array.from(areaCodes);
  }, [items]);

  const contextValue = useMemo(
    () => ({
      table: state.table,
      filters: state.filters,
      areaCodesOptions,
      loading: loadingTable,
      error: error?.message ?? null,
      refetch,
      tenantName: accountName,
    }),
    [state, areaCodesOptions, error, refetch, accountName, loadingTable],
  );

  return (
    <OutboundCallerIdTableContext.Provider value={contextValue}>
      {children}
    </OutboundCallerIdTableContext.Provider>
  );
};
